import { useState } from "react";
import {
  Award,
  Calendar,
  Car,
  ChevronLeft,
  ChevronRight,
  Expand,
  Fuel,
  Gauge,
  Navigation2,
  Palette,
  Settings,
  Pencil,
  Check,
  X,
  Key,
  Disc,
  History,
  Fingerprint,
  ShieldCheck,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

export type AuctionImage = {
  url: string;
  thumbnailUrl?: string | null;
  altText?: string | null;
};

export type AuctionVehicle = {
  make?: string | null;
  model?: string | null;
  variant?: string | null;
  vin?: string | null;
  modelYear?: number | null;
  mileage?: number | null;
  registrationNumber?: string | null;
  registrationDate?: string | Date | null;
  fuelType?: string | null;
  transmission?: string | null;
  bodyType?: string | null;
  exteriorColor?: string | null;
  interiorColor?: string | null;
  wheelDrive?: string | null;
  nextEuControl?: string | Date | null;
  conditionGrade?: string | null;
  conditionSummary?: string | null;
  technicalData?: any;
  keys?: string | null;
  tires?: string | null;
  tireCond?: string | null;
  service?: string | null;
  regDate?: string | null;
};

// ── Internal utils ────────────────────────────────────────────────────────────

function normalizeFuelType(v: string | null | undefined): string | null {
  if (!v) return null;
  const map: Record<string, string> = {
    ELECTRIC: "Elektrisk",
    GASOLINE: "Bensin",
    DIESEL: "Diesel",
    HYBRID: "Hybrid",
    PLUGIN_HYBRID: "Ladbar hybrid",
    HYDROGEN: "Hydrogen",
  };
  return map[v.toUpperCase()] ?? v;
}

function normalizeTransmission(v: string | null | undefined): string | null {
  if (!v) return null;
  const map: Record<string, string> = {
    AUTOMATIC: "Automat",
    MANUAL: "Manuell",
    SEMI_AUTOMATIC: "Semiautomat",
  };
  return map[v.toUpperCase()] ?? v;
}

function formatMileage(n: number): string {
  return n.toLocaleString("nb-NO") + " km";
}

function normalizeServiceHistory(v: string | null | undefined): string | null {
  if (!v) return null;
  const map: Record<string, string> = {
    ja: "Komplett",
    nei: "Mangler",
    vet_ikke: "Ukjent",
  };
  return map[v.toLowerCase()] ?? v;
}

function normalizeTireCondition(v: string | null | undefined): string | null {
  if (!v) return null;
  const map: Record<string, string> = {
    slitt: "Slitt",
    ok: "God",
    ny: "Nye",
  };
  return map[v.toLowerCase()] ?? v;
}

// ── ImageGallery ──────────────────────────────────────────────────────────────

export function ImageGallery({ images, title }: { images: AuctionImage[]; title: string }) {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const total = images.length;

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  if (total === 0) {
    return (
      <div
        className="flex h-80 items-center justify-center rounded-xl"
        style={{ background: "var(--muted)" }}
      >
        <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
          Ingen bilder
        </span>
      </div>
    );
  }

  const current = images[index];

  return (
    <>
      <div className="relative overflow-hidden rounded-xl bg-black" style={{ aspectRatio: "16/10" }}>
        <img src={current.url} alt={current.altText ?? title} className="h-full w-full object-contain" />

        {total > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        <div className="absolute bottom-3 left-3 rounded-md bg-black/50 px-2 py-0.5 text-xs text-white">
          {index + 1} / {total}
        </div>

        <button
          onClick={() => setLightbox(true)}
          className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-md bg-black/50 text-white transition-colors hover:bg-black/70"
        >
          <Expand className="h-4 w-4" />
        </button>
      </div>

      {total > 1 && (
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i: number) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className="flex-shrink-0 overflow-hidden rounded-md transition-opacity"
              style={{
                width: 72,
                height: 48,
                outline: i === index ? "2px solid var(--accent)" : "2px solid transparent",
                outlineOffset: 1,
                opacity: i === index ? 1 : 0.65,
              }}
            >
              <img src={img.thumbnailUrl ?? img.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <img
            src={current.url}
            alt={current.altText ?? title}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <div className="absolute bottom-6 text-sm text-white/60">{index + 1} / {total}</div>
        </div>
      )}
    </>
  );
}

// ── SpecGrid ──────────────────────────────────────────────────────────────────

interface SpecGridProps {
  vehicle: AuctionVehicle | null | undefined;
  onUpdateVehicle?: (fields: Partial<AuctionVehicle>) => Promise<void>;
  showAllForAdmin?: boolean;
}

export function SpecGrid({ vehicle, onUpdateVehicle, showAllForAdmin }: SpecGridProps) {
  const [editingKey, setEditingKey] = useState<keyof AuctionVehicle | null>(null);
  const [editValue, setEditValue] = useState("");

  if (!vehicle) return null;

  const specs = [
    { key: "modelYear" as const, label: "Modellår", value: vehicle.modelYear ? String(vehicle.modelYear) : null, Icon: Calendar },
    { key: "mileage" as const, label: "Kilometerstand", value: vehicle.mileage != null ? formatMileage(vehicle.mileage) : null, Icon: Gauge },
    { key: "fuelType" as const, label: "Drivstoff", value: normalizeFuelType(vehicle.fuelType), Icon: Fuel },
    { key: "transmission" as const, label: "Girkasse", value: normalizeTransmission(vehicle.transmission), Icon: Settings },
    { key: "bodyType" as const, label: "Karosseri", value: vehicle.bodyType, Icon: Car },
    { key: "wheelDrive" as const, label: "Hjuldrift", value: vehicle.wheelDrive, Icon: Navigation2 },
    { key: "exteriorColor" as const, label: "Farge", value: vehicle.exteriorColor, Icon: Palette },
    { key: "interiorColor" as const, label: "Interiør", value: vehicle.interiorColor, Icon: Palette },
    { key: "keys" as const, label: "Nøkler", value: vehicle.technicalData?.numberOfKeys, Icon: Key },
    { key: "tires" as const, label: "Hjulsett", value: vehicle.technicalData?.numberOfWheelSets ? `${vehicle.technicalData.numberOfWheelSets} sett` : null, Icon: Disc },
    { key: "tireCond" as const, label: "Dekktilstand", value: normalizeTireCondition(vehicle.technicalData?.tireCondition), Icon: Disc },
    { key: "service" as const, label: "Service", value: normalizeServiceHistory(vehicle.technicalData?.serviceHistory), Icon: History },
    { key: "regDate" as const, label: "Førstegangsreg.", value: vehicle.registrationDate ? new Date(vehicle.registrationDate).toLocaleDateString("nb-NO", { year: "numeric", month: "long" }) : null, Icon: Calendar },
    { key: "nextEuControl" as const, label: "Neste EU-kontroll", value: vehicle.nextEuControl ? new Date(vehicle.nextEuControl).toLocaleDateString("nb-NO", { year: "numeric", month: "long" }) : null, Icon: ShieldCheck },
    { key: "conditionGrade" as const, label: "Tilstand", value: vehicle.conditionGrade, Icon: Award },
    { key: "vin" as const, label: "VIN", value: vehicle.vin, Icon: Fingerprint },
  ];

  const filteredSpecs = specs.filter(s => {
    // If showAllForAdmin is true, we always show keys, tires, tireCond, and service
    if (showAllForAdmin && ["keys", "tires", "tireCond", "service"].includes(s.key)) return true;
    return s.value !== null && s.value !== undefined && s.value !== "";
  });

  const handleSave = async (key: keyof AuctionVehicle) => {
    if (!onUpdateVehicle) return;
    let finalValue: string | number | null = editValue;
    if (key === "modelYear" || key === "mileage") finalValue = parseInt(editValue, 10);
    await onUpdateVehicle({ [key]: finalValue });
    setEditingKey(null);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 lg:gap-4">
      {filteredSpecs.map(({ key, label, value, Icon }) => (
        <div
          key={label}
          className="rounded-2xl p-4 transition-all duration-300 relative group"
          style={{ background: "var(--muted)" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-xl p-2 shadow-sm" style={{ background: "white" }}>
                <Icon className="h-4 w-4" style={{ color: "var(--accent)" }} />
              </div>
              <span
                className="text-[10px] font-black uppercase tracking-widest"
                style={{ color: "var(--muted-foreground)" }}
              >
                {label}
              </span>
            </div>
            {onUpdateVehicle && editingKey !== key && (
              <button
                onClick={() => {
                  setEditingKey(key);
                  setEditValue(String(vehicle[key] ?? ""));
                }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-white/50 transition-all text-muted-foreground"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>

          {editingKey === key ? (
            <div className="flex items-center gap-1">
              <input
                type={(key === "modelYear" || key === "mileage") ? "number" : "text"}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                className="w-full bg-white border border-border rounded px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 focus:ring-accent"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave(key);
                  if (e.key === "Escape") setEditingKey(null);
                }}
              />
              <button onClick={() => handleSave(key)} className="text-green-600 hover:bg-white/50 p-1 rounded">
                <Check className="h-3 w-3" />
              </button>
              <button onClick={() => setEditingKey(null)} className="text-muted-foreground hover:bg-white/50 p-1 rounded">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <p className="text-sm font-bold" style={{ color: "var(--foreground)" }}>
              {value || "–"}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── AuctionOverview ───────────────────────────────────────────────────────────

export function AuctionOverview({
  title,
  images,
  vehicle,
  description,
  onUpdateTitle,
  onUpdateDescription,
  onUpdateVehicle,
  showAllForAdmin,
}: {
  title: string;
  images: AuctionImage[];
  vehicle?: AuctionVehicle | null;
  description?: string | null;
  onUpdateTitle?: (title: string) => Promise<void>;
  onUpdateDescription?: (desc: string) => Promise<void>;
  onUpdateVehicle?: (fields: Partial<AuctionVehicle>) => Promise<void>;
  showAllForAdmin?: boolean;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(title);

  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(description ?? "");

  const hasSpecs =
    vehicle &&
    (vehicle.modelYear ||
      vehicle.mileage != null ||
      vehicle.fuelType ||
      vehicle.transmission ||
      vehicle.bodyType ||
      vehicle.exteriorColor ||
      vehicle.wheelDrive ||
      vehicle.conditionGrade ||
      onUpdateVehicle);

  const handleTitleSave = async () => {
    if (!onUpdateTitle) return;
    await onUpdateTitle(titleValue);
    setEditingTitle(false);
  };

  const handleDescSave = async () => {
    if (!onUpdateDescription) return;
    await onUpdateDescription(descValue);
    setEditingDesc(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <ImageGallery images={images} title={title} />

      <div className="flex flex-col gap-2 group relative">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              className="text-2xl font-black w-full bg-white border border-border rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-accent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitleSave();
                if (e.key === "Escape") setEditingTitle(false);
              }}
            />
            <button onClick={handleTitleSave} className="text-green-600 hover:bg-muted p-2 rounded">
              <Check className="h-5 w-5" />
            </button>
            <button onClick={() => setEditingTitle(false)} className="text-muted-foreground hover:bg-muted p-2 rounded">
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <h2
              className="text-2xl font-black leading-tight"
              style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
            >
              {title}
            </h2>
            {onUpdateTitle && (
              <button
                onClick={() => {
                  setTitleValue(title);
                  setEditingTitle(true);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-muted transition-all text-muted-foreground"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        {vehicle?.registrationNumber && (
          <span
            className="inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-mono font-bold"
            style={{
              background: "var(--muted)",
              color: "var(--muted-foreground)",
              border: "1px solid var(--border)",
            }}
          >
            {vehicle.registrationNumber}
          </span>
        )}
      </div>

      {hasSpecs && (
        <div
          className="rounded-2xl p-5 sm:p-6 shadow-sm"
          style={{ background: "white", border: "1px solid var(--border)" }}
        >
          <h3
            className="mb-5 text-lg font-bold"
            style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
          >
            Spesifikasjoner
          </h3>
          <SpecGrid vehicle={vehicle} onUpdateVehicle={onUpdateVehicle} showAllForAdmin={showAllForAdmin} />
          {(vehicle?.conditionSummary || onUpdateVehicle) && (
            <div
              className="mt-5 rounded-xl p-4 relative group"
              style={{ background: "var(--muted)", border: "1px dashed var(--border)" }}
            >
              <div className="flex justify-between items-start mb-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Tilstandssammendrag</span>
                {onUpdateVehicle && (
                  <button
                    onClick={async () => {
                      const val = prompt("Oppdater tilstandssammendrag:", vehicle?.conditionSummary ?? "");
                      if (val !== null) await onUpdateVehicle({ conditionSummary: val });
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-white/50 transition-all text-muted-foreground"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
              <p className="text-sm leading-relaxed italic" style={{ color: "var(--muted-foreground)" }}>
                "{vehicle?.conditionSummary || "–"}"
              </p>
            </div>
          )}
        </div>
      )}

      {(description || onUpdateDescription) && (
        <div
          className="rounded-2xl p-5 sm:p-6 shadow-sm group relative"
          style={{ background: "white", border: "1px solid var(--border)" }}
        >
          <div className="mb-4 flex items-center justify-between">
            <h3
              className="text-lg font-bold"
              style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
            >
              Beskrivelse
            </h3>
            {onUpdateDescription && !editingDesc && (
              <button
                onClick={() => {
                  setDescValue(description ?? "");
                  setEditingDesc(true);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-muted transition-all text-muted-foreground"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>

          {editingDesc ? (
            <div className="flex flex-col gap-2">
              <textarea
                value={descValue}
                onChange={(e) => setDescValue(e.target.value)}
                className="w-full min-h-[150px] bg-background border border-border rounded-lg p-3 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingDesc(false)} className="text-sm font-medium text-muted-foreground px-3 py-1 hover:bg-muted rounded">Avbryt</button>
                <button onClick={handleDescSave} className="text-sm font-bold text-white bg-accent px-4 py-1 rounded shadow-sm hover:opacity-90">Lagre</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              {(description || "–").split("\n").map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
